const EARLIEST_META_TIMESTAMP = Date.UTC(2000, 0, 1);
const CLOCK_SKEW_MS = 5 * 60 * 1000;

export function validFbclid(value: string | null | undefined): string | null {
  const id = value?.trim();
  if (!id || id.length > 500 || !/^[A-Za-z0-9_-]+$/.test(id) ||
      /^(?:fbclid|fbc|undefined|null|test|sample|example|placeholder)(?:[-_0-9].*)?$/i.test(id)) return null;
  return id;
}

export function validFbc(value: string | null | undefined, now = Date.now()): string | null {
  const fbc = value?.trim();
  const match = fbc?.match(/^fb\.(\d+)\.(\d{13})\.([A-Za-z0-9_-]+)(?:\.(?:AQ|Ag|Aw|BA|BQ|Bg|[A-Za-z0-9_-]{8}))?$/);
  if (!fbc || !match || !validFbclid(match[3])) return null;
  const timestamp = Number(match[2]);
  return timestamp >= EARLIEST_META_TIMESTAMP && timestamp <= now + CLOCK_SKEW_MS ? fbc : null;
}

export function validFbp(value: string | null | undefined, now = Date.now()): string | null {
  const fbp = value?.trim();
  const match = fbp?.match(/^fb\.(\d+)\.(\d{13})\.\d+(?:\.(?:AQ|Ag|Aw|BA|BQ|Bg|[A-Za-z0-9_-]{8}))?$/);
  if (!fbp || !match) return null;
  const timestamp = Number(match[2]);
  return timestamp >= EARLIEST_META_TIMESTAMP && timestamp <= now + CLOCK_SKEW_MS ? fbp : null;
}

/** Preserve the click and its original creation time across page loads and checkout. */
export function resolveFbc(
  urlFbc: string | null,
  urlFbclid: string | null,
  cookieFbc: string | null,
  now = Date.now(),
): string | null {
  const imported = validFbc(urlFbc, now);
  const cookie = validFbc(cookieFbc, now);
  const click = validFbclid(urlFbclid);
  if (!click) return imported ?? cookie;
  // A fresh ad click takes precedence over attribution carried by an older link.
  if (cookie?.split(".")[3] === click) return cookie;
  if (imported?.split(".")[3] === click) return imported;
  return `fb.1.${now}.${click}`;
}

type MetaCookie = "fbp" | "fbc";

export function readMetaCookie(name: MetaCookie): string | null {
  try {
    const validate = name === "fbp" ? validFbp : validFbc;
    // An old host-only cookie may coexist with a newer parent-domain cookie.
    for (const part of document.cookie.split(";")) {
      const match = part.trim().match(new RegExp(`^_${name}=(.*)$`));
      if (!match) continue;
      try {
        const value = validate(decodeURIComponent(match[1]));
        if (value) return value;
      } catch { /* Malformed cookie. */ }
    }
  } catch { /* Cookies can be unavailable in an embedded browser. */ }
  return null;
}

export function clearMetaCookie(name: MetaCookie): void {
  try {
    document.cookie = `_${name}=;path=/;max-age=0`;
    const labels = window.location.hostname.split(".");
    while (labels.length > 1) {
      document.cookie = `_${name}=;path=/;max-age=0;domain=.${labels.join(".")}`;
      labels.shift();
    }
  } catch { /* Optional storage must not affect checkout. */ }
}

/** Use the same scope encoded in the cookie that Meta's SDK will use. */
export function writeMetaCookie(name: MetaCookie, value: string): void {
  const validated = (name === "fbp" ? validFbp : validFbc)(value);
  if (!validated) return;
  try {
    const labels = window.location.hostname.split(".");
    const index = Number(validated.split(".")[1]);
    const domain = Number.isInteger(index) && index > 0 && index < labels.length
      ? `;domain=.${labels.slice(-1 - index).join(".")}` : "";
    clearMetaCookie(name);
    document.cookie = `_${name}=${encodeURIComponent(validated)};path=/;max-age=7776000;SameSite=Lax${window.location.protocol === "https:" ? ";Secure" : ""}${domain}`;
  } catch { /* Optional storage must not affect checkout. */ }
}

/** Run before the vendor pixel reads landing parameters or existing cookies. */
export function cleanMetaLandingAttribution(writeCookies = false): void {
  const url = new URL(window.location.href);
  const original = url.href;
  for (const name of ["fbc", "meta_fbc", "fbp", "meta_fbp"]) {
    const valid = name.endsWith("fbc") ? validFbc : validFbp;
    if (url.searchParams.has(name) && !valid(url.searchParams.get(name))) url.searchParams.delete(name);
  }
  if (url.searchParams.has("fbclid") && !validFbclid(url.searchParams.get("fbclid"))) url.searchParams.delete("fbclid");
  for (const name of writeCookies ? ["fbp", "fbc"] as const : []) {
    const cookie = readMetaCookie(name);
    const imported = url.searchParams.get(name) ?? url.searchParams.get(`meta_${name}`);
    const value = name === "fbc" ? resolveFbc(imported, url.searchParams.get("fbclid"), cookie) : cookie ?? validFbp(imported);
    // Normalize legacy host-only copies before the SDK starts writing cookies.
    if (value) writeMetaCookie(name, value);
    else clearMetaCookie(name);
  }
  if (url.href !== original) window.history.replaceState(window.history.state, "", url.href);
}
